// PIN entry screen — manual fallback from the primary presence QR surface.
// The production PIN flow uses the
// ADR-020 device credential plus the employee's six-digit kiosk PIN. Layout
// follows the mockup's centered-headline + bottom-bar grammar.

import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PinPad } from "@/components/PinPad";

export interface PinScreenProps {
  readonly pin: string;
  readonly canSubmit: boolean;
  readonly onDigit: (digit: string) => void;
  readonly onBackspace: () => void;
  readonly onSubmit: () => void;
  readonly onBack: () => void;
}

export function PinScreen({
  pin,
  canSubmit,
  onDigit,
  onBackspace,
  onSubmit,
  onBack,
}: PinScreenProps): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-0 flex-1 flex-col px-6 pb-4 sm:px-16 sm:pb-9">
      <div className="flex flex-1 flex-col items-center justify-center gap-3 sm:gap-7">
        <div className="text-center">
          <p className="mb-1.5 font-sans text-[12px] font-bold uppercase tracking-[0.1em] text-[var(--accent)]">
            {t("kiosk.pin.eyebrow")}
          </p>
          <h1 className="m-0 font-serif text-[32px] font-normal tracking-[-0.015em] text-[var(--fg-primary)] sm:text-[44px]">
            {t("kiosk.pin.title")} <em className="italic">{t("kiosk.pin.title_em")}</em>
          </h1>
        </div>

        <PinPad
          pin={pin}
          canSubmit={canSubmit}
          onDigit={onDigit}
          onBackspace={onBackspace}
          onSubmit={onSubmit}
        />

        <p className="m-0 text-center font-serif text-[15px] italic text-[var(--fg-muted)] sm:text-[16px]">
          {t("kiosk.pin.hint")}
        </p>
      </div>

      {/* Bottom bar — mockup KioskVisitorForm's footer grammar */}
      <div className="flex shrink-0 items-center border-t border-[var(--border-subtle)] pt-3 sm:pt-4">
        <button
          type="button"
          onClick={onBack}
          className="flex min-h-11 cursor-pointer items-center gap-2 rounded-[14px] border border-[var(--border-default)] bg-[var(--bg-raised)] px-6 py-2 font-sans text-[16px] font-medium text-[var(--fg-secondary)] transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] active:bg-[var(--bg-subtle)] sm:px-9 sm:py-[18px] sm:text-[18px]"
        >
          <ArrowLeft size={20} aria-hidden="true" className="text-[var(--fg-muted)]" />
          {t("kiosk.pin.back")}
        </button>
      </div>
    </div>
  );
}
